import {
  getTheme,
  Icon,
  Link,
  Stack,
  Text,
  TextField,
  Toggle
} from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, type Resolver, useForm } from "react-hook-form";

import { physicalChipDeliveryFormDefaultValues } from "../../constants";
import type { PhysicalChipDeliveryFormValues } from "../../interfaces";
import { physicalChipDeliveryFormSchema } from "../../schema/PhysicalChipDeliveryForm.schema";

interface PhysicalChipDeliveryFormProps {
  formId: string;
  isSubmitting: boolean;
  onSubmit: (values: PhysicalChipDeliveryFormValues) => void;
  onCancel: () => void;
}

export function PhysicalChipDeliveryForm({
  formId,
  isSubmitting,
  onSubmit,
  onCancel
}: PhysicalChipDeliveryFormProps): JSX.Element {
  const theme = getTheme();
  const form = useForm<PhysicalChipDeliveryFormValues>({
    resolver: yupResolver(
      physicalChipDeliveryFormSchema
    ) as Resolver<PhysicalChipDeliveryFormValues>,
    defaultValues: physicalChipDeliveryFormDefaultValues
  });
  const { control, handleSubmit, watch } = form;
  const deliveryByMail = watch("deliveryByMail");

  return (
    <section
      style={{
        border: `1px solid ${theme.palette.neutralLight}`,
        borderRadius: 6,
        padding: 14
      }}
    >
      <form id={formId} onSubmit={event => void handleSubmit(onSubmit)(event)}>
        <Stack tokens={{ childrenGap: 14 }}>
          <Stack tokens={{ childrenGap: 4 }}>
            <Link
              href="#"
              onClick={event => {
                event.preventDefault();

                if (!isSubmitting) onCancel();
              }}
              disabled={isSubmitting}
              styles={{
                root: {
                  alignItems: "center",
                  display: "inline-flex",
                  gap: 4,
                  textDecoration: "none",
                  width: "fit-content"
                }
              }}
            >
              <Icon
                iconName="ChevronLeft"
                styles={{
                  root: {
                    fontSize: 8,
                    fontWeight: 600,
                    marginTop: 2
                  }
                }}
              />
              <span className="ms-fontSize-12">Voltar</span>
            </Link>

            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Confirmar entrega do chip físico
            </Text>
            <Text variant="small">
              Informe os dados utilizados para validar e registrar a entrega.
            </Text>
          </Stack>

          <Controller
            name="iccid"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="ICCID"
                inputMode="numeric"
                maxLength={22}
                value={field.value}
                onChange={(_, value) =>
                  field.onChange((value ?? "").replace(/\D/g, "").slice(0, 22))
                }
                errorMessage={fieldState.error?.message}
                disabled={isSubmitting}
                required
              />
            )}
          />

          <Controller
            name="deliveryByMail"
            control={control}
            render={({ field }) => (
              <Toggle
                label="Entrega pelos Correios"
                checked={field.value}
                onChange={(_, checked) => field.onChange(Boolean(checked))}
                onText="Sim"
                offText="Não"
                disabled={isSubmitting}
              />
            )}
          />

          {deliveryByMail && (
            <Controller
              name="trackingCode"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  label="Código de rastreamento"
                  value={field.value}
                  onChange={(_, value) => field.onChange(value ?? "")}
                  errorMessage={fieldState.error?.message}
                  disabled={isSubmitting}
                  required
                />
              )}
            />
          )}

          <Controller
            name="rescueProtocol"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Protocolo de resgate"
                value={field.value}
                onChange={(_, value) => field.onChange(value ?? "")}
                errorMessage={fieldState.error?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </Stack>
      </form>
    </section>
  );
}
